const AVATAR_SIZE = 256;
const WEBP_QUALITY = 0.8;

const CHAT_IMAGE_MAX_DIMENSION = 1536;
const CHAT_IMAGE_QUALITY = 0.85;
const CHAT_IMAGE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Loads a File or Blob as an HTMLImageElement.
 * Revokes the object URL after the image loads to avoid memory leaks.
 */
const loadImage = (file: File | Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });

/**
 * Center-crops and resizes an image file to a 256x256 WebP blob.
 * Uses the Canvas API -- no external dependencies required.
 */
export const processAvatarImage = async (file: File): Promise<Blob> => {
  const img = await loadImage(file);

  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = AVATAR_SIZE;
  canvas.height = AVATAR_SIZE;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");

  ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to export canvas to WebP"));
      },
      "image/webp",
      WEBP_QUALITY,
    );
  });
};

/**
 * Resizes an image to fit within {@link CHAT_IMAGE_MAX_DIMENSION}px on its
 * longest side (preserving aspect ratio) and compresses to WebP. If the
 * result exceeds {@link CHAT_IMAGE_MAX_BYTES}, progressively lowers quality.
 */
export const compressChatImage = async (file: File): Promise<Blob> => {
  const img = await loadImage(file);

  let { naturalWidth: w, naturalHeight: h } = img;
  if (w > CHAT_IMAGE_MAX_DIMENSION || h > CHAT_IMAGE_MAX_DIMENSION) {
    const scale = CHAT_IMAGE_MAX_DIMENSION / Math.max(w, h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");
  ctx.drawImage(img, 0, 0, w, h);

  const toBlob = (quality: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to export canvas to WebP"));
        },
        "image/webp",
        quality,
      );
    });

  let blob = await toBlob(CHAT_IMAGE_QUALITY);

  let quality = CHAT_IMAGE_QUALITY;
  while (blob.size > CHAT_IMAGE_MAX_BYTES && quality > 0.3) {
    quality -= 0.1;
    blob = await toBlob(quality);
  }

  return blob;
};

/** Converts a Blob to a data URL string. */
export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read blob as data URL"));
    reader.readAsDataURL(blob);
  });
